import React, { CSSProperties, useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  Button,
  Text,
} from "@coreai/component-library";
import { css, useTheme } from "@emotion/react";
import { Blocker, useBlocker } from "react-router-dom";
import "./NavigationBlocker.css";


interface NavigationBlockerProps {
    shouldBlock: boolean;
    onClose: () => void;
    dialogTitle: string;
    dialogMessage: string;
    onCancel: () => void;
    cancelText: string;
    onProceed: () => void;
    proceedText: string;
    descrdText: string;
    onDiscard: () => void;
    diableProceed?: boolean;
}

function NavigationBlocker({
    shouldBlock,
    onClose,
    dialogTitle,
    dialogMessage,
    onCancel,
    cancelText,
    onProceed,
    proceedText,
    onDiscard,
    descrdText,
    diableProceed = false,
}: NavigationBlockerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const theme = useTheme();
    const alertDialogBodyStyles = css`
    border: none;
    display: flex;
  
    @media (max-width: 450px) {
      flex-direction: column;
    }
  `;

    const bodyStyles = {
        fontFamily: theme.typography.bodyMedium.fontFamily,
        fontSize: theme.typography.bodyMedium.fontSize,
        fontWeight: theme.typography.bodyMedium.fontWeight,
        lineHeight: theme.typography.bodyMedium.lineHeight,
        color: theme.colors.textDark,
        marginBottom: theme.spacing.sm,
        paddingTop: 0,
        overflowY: "visible",
        gap: "1.5rem",
    } as CSSProperties;

    let blocker: Blocker = useBlocker(({ currentLocation, nextLocation }) =>
        shouldBlock &&
        currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        setIsOpen(blocker.state === "blocked");
    }, [blocker.state]);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = ""; // Required for modern browsers      
        };
        if(shouldBlock){
            window.addEventListener("beforeunload", handleBeforeUnload);
        }else{
            window.removeEventListener("beforeunload", handleBeforeUnload);
        }
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [shouldBlock]);

    return (
        <AlertDialog
            open={isOpen}
            onOpenChange={(_isOpen) => {
                if (!_isOpen && blocker.state === "blocked") {
                    onClose();
                    blocker.reset();
                }
            }}
        >
            <AlertDialogContent size="sm">
                <AlertDialogHeader className='alertDialogBodyStyles'>
                    <AlertDialogTitle>
                        {dialogTitle}
                    </AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogBody style={bodyStyles}>
                    <Text style={bodyStyles}>          
                        {dialogMessage}
                    </Text>
                </AlertDialogBody>

                <AlertDialogFooter className='alertDialogFooter'>
                    <AlertDialogCancel asChild>
                        <Button
                            variant="tertiary"
                            onClick={() => {
                                onCancel();
                                if (blocker.state === "blocked") blocker.reset();
                            }}    
                        >
                            {cancelText}
                        </Button>
                    </AlertDialogCancel>
                    <AlertDialogCancel asChild>
                        <Button
                            variant="tertiary"
                            onClick={() => {
                                onDiscard();
                                if (blocker.state === "blocked") blocker.proceed();
                            }}    
                        >
                            {descrdText}
                        </Button>
                    </AlertDialogCancel>
                    <AlertDialogAction asChild>
                        <Button
                            variant={"brand"}
                            style={{ backgroundColor: theme.colors.primary }}
                            disabled={diableProceed}
                            onClick={() => {
                                onProceed();
                                if (blocker.state === "blocked") blocker.proceed();
                            }}
                            className="brandPrimaryBtn"
                        >
                            {proceedText}
                        </Button>
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
export default NavigationBlocker;